// src/components/Widgets/ReorderPOWidget.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateTo, ROUTES } from "../../lib/nav";
import WidgetHeader from "../Common/WidgetHeader";
import { useMaterials } from "../../contexts/MaterialContext";

/* ---------- helpers ---------- */
const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const clamp1 = (n) => Math.max(1, toNum(n) || 1);

// Current on-hand stock (aliases supported)
const getStock = (m) =>
  toNum(m?.stock ?? m?.stockQty ?? m?.startQuantity ?? m?.quantity ?? 0);

// Units
const getUnit = (m) => m?.trackingUnit || m?.conversionUnit || m?.unit || "unit";

// Unit cost respecting conversionAmount if present
const getUnitCost = (m) => {
  const cost = toNum(m?.purchaseCost);
  const conv = Math.max(1, toNum(m?.conversionAmount) || 1);
  return conv > 0 ? cost / conv : 0;
};

// Figure out “how much is already spoken for”
const getReserved = (m) =>
  toNum(m?.reserved) ||
  toNum(m?.allocated) ||
  toNum(m?.allocatedQty) ||
  toNum(m?.reservedQty) ||
  toNum(m?.pendingUse) ||
  toNum(m?.workshopDemand) ||
  0;

// Projected stock = on-hand minus what’s already allocated
const getProjectedStock = (m) => Math.max(0, getStock(m) - getReserved(m));

// Reorder point aliases (old data used different names)
const getReorderPoint = (m) => {
  const aliases = [
    "reorderPoint",
    "reorderLevel",
    "reorderQty",
    "reorderThreshold",
    "reorder_at",
    "minStock",
    "minQty",
    "minimumQty",
    "minStockLevel",
    "safetyStock",
  ];
  for (const k of aliases) {
    const v = toNum(m?.[k]);
    if (v > 0) return v;
  }
  return 0;
};

export default function ReorderPOWidget({
  isDark = true,
  backTo = "/dashboard",
  currency = "AUD",
}) {
  const navigate = useNavigate();
  const { materials = [], scrapedMaterials = [] } = useMaterials() || {};
  const inv = (scrapedMaterials?.length ? scrapedMaterials : materials) || [];

  const fmt = useMemo(() => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        currencyDisplay: "symbol",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      // Fallback if currency code is invalid/missing
      return { format: (n) => `$${Number(n || 0).toFixed(2)}` };
    }
  }, [currency]);

  // Low stock computation (based on PROJECTED stock vs reorder point)
  const rows = useMemo(() => {
    return inv
      .map((m) => {
        const stock = getStock(m);
        const reserved = getReserved(m);
        const projected = getProjectedStock(m);
        const reorderPoint = getReorderPoint(m);

        if (!(reorderPoint > 0) || projected > reorderPoint) return null;

        // bring *projected* up to 1.5x reorder point (simple heuristic)
        const target = Math.ceil(reorderPoint * 1.5);
        const qty = clamp1(target - projected);

        return {
          id: String(m.id || m.sku || m.name || Math.random()),
          materialId: m.id,
          name: m.name || "Unnamed",
          sku: m.sku || "",
          image: typeof m.image === "string" ? m.image : null,
          supplier: m.supplierName || m.supplier || "",
          unit: getUnit(m),
          unitCost: getUnitCost(m),
          category: m.category || "",
          stock, // on-hand
          reserved, // spoken-for
          projected, // on-hand minus reserved
          reorderPoint,
          qty,
          website: m.website || "",
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [inv]);

  const [selected, setSelected] = useState(() => new Set());
  const [qtyEdits, setQtyEdits] = useState({}); // id -> qty override

  const toggle = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectAll = () => setSelected(new Set(rows.map((r) => r.id)));
  const clearSel = () => setSelected(new Set());

  const setQty = (id, v) =>
    setQtyEdits((q) => ({ ...q, [id]: clamp1(v) }));

  const effectiveRows = rows.map((r) => ({
    ...r,
    qty: qtyEdits[r.id] != null ? qtyEdits[r.id] : r.qty,
  }));

  const selectedRows = effectiveRows.filter((r) => selected.has(r.id));
  const countSelected = selectedRows.length;

  const totals = useMemo(() => {
    const allLines = effectiveRows.length;
    const allTotal = effectiveRows.reduce(
      (a, r) => a + (toNum(r.unitCost) * clamp1(r.qty)),
      0
    );
    const selLines = countSelected;
    const selTotal = selectedRows.reduce(
      (a, r) => a + (toNum(r.unitCost) * clamp1(r.qty)),
      0
    );
    return { allLines, allTotal, selLines, selTotal };
  }, [effectiveRows, selectedRows, countSelected]);

  // Optional existing POs (if you persist them)
  const openPOs = useMemo(() => {
    try {
      const arr = JSON.parse(localStorage.getItem("mudoPOs") || "[]");
      return (Array.isArray(arr) ? arr : []).filter((po) =>
        ["Open", "Submitted", "Pending"].includes(po.status)
      );
    } catch {
      return [];
    }
  }, []);

  const createDraftPO = (useAll = false) => {
    const source = useAll ? effectiveRows : selectedRows;
    if (!source.length) return;

    // Grouping by supplier later in PO page; here we seed a single draft
    const firstSupplier = source[0].supplier || "";
    const items = source.map((l) => ({
      materialId: l.materialId || "",
      name: l.name,
      sku: l.sku || "",
      supplier: l.supplier || firstSupplier,
      website: l.website || "",
      image: l.image || null,
      unit: l.unit,
      unitCost: Number(l.unitCost || 0),
      qty: Number(l.qty || 1),
      category: l.category || "",
      notes: "From Reorder widget",
    }));

    const draft = {
      id: `draft-${Date.now()}`,
      status: "Draft",
      createdAt: Date.now(),
      supplier: firstSupplier,
      items,
      meta: { source: "reorder-widget", currency },
    };

    try {
      localStorage.setItem("mudoDraftPO", JSON.stringify(draft));
    } catch {}
    navigateTo(navigate, ROUTES.PURCHASE_ORDERS, { source: 'ReorderPOWidget:createPO' });
  };

  const sectionCard = isDark
    ? "rounded border p-3 bg-white/5 border-white/10"
    : "rounded border p-3 bg-white border-zinc-200";

  return (
    <div className="space-y-3">
      <WidgetHeader
        title="Reorder & POs"
        to={backTo}
        right={
          <button
            onClick={() => navigateTo(navigate, ROUTES.PURCHASE_ORDERS, { source: 'ReorderPOWidget:openPOs' })}
            className="px-3 py-1 text-sm rounded text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            Open POs
          </button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-2 text-sm tabular-nums">
        <div className={sectionCard}>
          <div className="text-[11px] opacity-70">Low stock items</div>
          <div className="text-xl font-semibold">{rows.length}</div>
        </div>
        <div className={sectionCard}>
          <div className="text-[11px] opacity-70">Selected</div>
          <div className="text-xl font-semibold">{countSelected}</div>
        </div>
        <div className={sectionCard}>
          <div className="text-[11px] opacity-70">Open POs</div>
          <div className="text-xl font-semibold">{openPOs.length}</div>
        </div>
      </div>

      {/* Totals */}
      <div className={`flex items-center justify-between ${sectionCard}`}>
        <div className="text-sm">
          <div className="opacity-70 text-[11px]">Totals (All low-stock)</div>
          <div className="tabular-nums">{totals.allLines} lines • {fmt.format(totals.allTotal)}</div>
        </div>
        <div className="text-sm text-right">
          <div className="opacity-70 text-[11px]">Totals (Selected)</div>
          <div className="font-semibold tabular-nums">{totals.selLines} lines • {fmt.format(totals.selTotal)}</div>
        </div>
      </div>

      {/* Low stock list */}
      <div className={sectionCard}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-medium text-sm">Low stock</div>
          <div className="flex items-center gap-2">
            <button
              className="text-xs underline"
              onClick={selectAll}
              disabled={!rows.length}
              title="Select all"
            >
              Select all
            </button>
            <button
              className="text-xs underline"
              onClick={clearSel}
              disabled={!countSelected}
              title="Clear selection"
            >
              Clear
            </button>
            <button
              onClick={() => createDraftPO(false)}
              disabled={!countSelected}
              className={`text-xs px-2 py-1 rounded ${
                countSelected ? "bg-amber-500 text-black" : "bg-zinc-700 text-white/60 cursor-not-allowed"
              }`}
              title="Create a draft PO with selected items"
            >
              Draft PO (Selected)
            </button>
            <button
              onClick={() => createDraftPO(true)}
              disabled={!rows.length}
              className={`text-xs px-2 py-1 rounded ${
                rows.length ? "bg-emerald-500 text-black" : "bg-zinc-700 text-white/60 cursor-not-allowed"
              }`}
              title="Create a draft PO with all low-stock items"
            >
              Draft PO (All)
            </button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-xs opacity-70">
            All good — nothing below reorder levels.{" "}
            <button
              onClick={() => navigateTo(navigate, ROUTES.MATERIALS, { source: 'ReorderPOWidget:checkMaterials' })}
              className="underline"
              title="Open Materials"
            >
              Check materials →
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {effectiveRows.map((r) => (
              <div
                key={r.id}
                className="grid grid-cols-12 items-center gap-2 text-xs border border-white/10 rounded p-2"
              >
                <div className="col-span-1">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggle(r.id)}
                    aria-label={`Select ${r.name}`}
                  />
                </div>

                <div className="col-span-5 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {r.image ? (
                      <img
                        src={r.image}
                        alt=""
                        className="h-8 w-8 object-cover rounded border border-white/10 shrink-0"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded bg-white/10 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="font-medium truncate" title={r.name}>{r.name}</div>
                      <div className="opacity-60 truncate">
                        {r.sku ? `${r.sku} • ` : ""}Stock {r.stock}
                        {r.reserved ? ` (−${r.reserved} reserved)` : ""} • RP {r.reorderPoint}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-span-3">
                  <label className="block opacity-70">Qty to order</label>
                  <input
                    type="number"
                    min={1}
                    value={r.qty}
                    onChange={(e) => setQty(r.id, e.target.value)}
                    className="w-full px-2 py-1 rounded bg-zinc-900 border border-white/10"
                  />
                  <div className="mt-1 opacity-60">
                    {fmt.format(r.unitCost)} / {r.unit}
                  </div>
                </div>

                <div className="col-span-3 text-right tabular-nums">
                  <div className="opacity-70">Est. line cost</div>
                  <div className="font-semibold">
                    {fmt.format((toNum(r.unitCost) || 0) * clamp1(r.qty))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Open POs preview (optional) */}
      {openPOs.length > 0 && (
        <div className={sectionCard}>
          <div className="font-medium text-sm mb-2">Open POs</div>
          <ul className="text-xs space-y-1">
            {openPOs.slice(0, 5).map((po) => (
              <li key={po.id} className="flex items-center justify-between">
                <span className="truncate">
                  #{po.id} • {po.supplier || "Supplier"} • {po.status}
                </span>
                <button
                  className="text-xs underline"
                  onClick={() => navigateTo(navigate, ROUTES.PURCHASE_ORDERS, { source: 'ReorderPOWidget:viewPO' })}
                >
                  View
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
